/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { ActionButton, Modal, Text } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import React, { useMemo } from 'react';
import { getConfig } from '../../../utils/configs';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';

const modalStyles = StyleSheet.create({
  message: {
    marginTop: 16,
    marginBottom: 16,
  },
  documentList: {
    marginBottom: 8,
  },
  documentName: {
    marginLeft: 16,
    marginBottom: 4,
  },
  additionalDocuments: {
    marginBottom: 24,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
});

export namespace DocumentsPendingTaggingModal {
  export interface Props {
    isOpen: boolean;
    documentsPendingTaggingNames: string[];
    roomId: string;
    handleClose: () => void;
  }
}

export const DocumentsPendingTaggingModal: React.FC<DocumentsPendingTaggingModal.Props> = ({
  isOpen,
  documentsPendingTaggingNames,
  roomId,
  handleClose,
}) => {
  const sortedDocuments = useMemo(() => {
    if (!documentsPendingTaggingNames || documentsPendingTaggingNames.length === 0) {
      return [];
    }
    return [...documentsPendingTaggingNames].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [documentsPendingTaggingNames]);

  const hasDocuments = documentsPendingTaggingNames?.length > 0;
  const modalIsOpen = isOpen && hasDocuments;

  const modalTitle =
    documentsPendingTaggingNames?.length === 1 ? 'Document Pending Tagging' : 'Documents Pending Tagging';

  const isDismissible = getConfig().enableDismissibleTaggedDocumentsModal20251114 ?? true;
  const handleModalClose = isDismissible ? handleClose : () => {};

  return (
    <Modal
      data-it="DocumentPendingTaggingModal"
      contentLabel={modalTitle}
      isOpen={modalIsOpen}
      handleClose={handleModalClose}
    >
      {hasDocuments && (
        <ModalContent headerText={modalTitle} {...(isDismissible && { handleClose })}>
          <div className={css(modalStyles.message)}>
            <Text>
              {documentsPendingTaggingNames?.length === 1 ? (
                <>
                  <p>We've detected a new document that needs to be tagged before signing can continue.</p>
                  <p>Please click the Manage Documents button to tag the new file.</p>
                </>
              ) : (
                <>
                  <p>We've detected new documents that need to be tagged before signing can continue.</p>
                  <p>Please click the Manage Documents button to tag the new files.</p>
                </>
              )}
            </Text>
          </div>
          <div className={css(modalStyles.documentList)}>
            {/* There are expected to be a limited number of documents and do not have requirements to limit the number being displayed */}
            {sortedDocuments.map((name, index) => (
              <div key={index} className={css(modalStyles.documentName)}>
                <Text weight="bold">
                  {index + 1}. {name}
                </Text>
              </div>
            ))}
          </div>
          <div className={css(modalStyles.message)}>
            <Text>This modal will automatically dismiss once all pending documents are tagged.</Text>
          </div>
          <div className={css(modalStyles.buttonContainer)}>
            <ActionButton
              data-dd-privacy="allow"
              btnSize="md"
              label="Manage Documents"
              type="button"
              onClick={() => window.open(`/notary/rooms/${roomId}/documents/manage`)}
              isFullWidth={false}
            />
          </div>
        </ModalContent>
      )}
    </Modal>
  );
};
